import React, {useCallback, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {PrimaryOffer} from 'components/Offer/Primary'
import {SecondaryOfferList} from 'components/OfferList/SecondaryOfferList'
import {MoreDealsButtonVertical} from 'components/OfferList/SecondaryOfferList/MoreDealsButton/MoreDealsButtonVertical'
import {PriceWatchCTAWrapper} from 'components/PriceWatchCTAWrapper/PriceWatchCTAWrapper'
import {getSecondaryOffers} from 'components/SplitBookings/helpers'
import {useOfferContainer} from 'hooks/useOfferContainer'
import {
  getAnchorComplete,
  getHotel,
  getIsAnchorHotel,
  getSplitBookingDetails
} from 'modules/sapiSearch/selectors'
import {HotelOfferEntity} from 'modules/sapiSearch/slice'

import {cssTheme} from '@vio/atlas/themes'
import {Page} from '@vio/core/src/analytics/types/Events'
import {OfferPrimaryLayoutVariant} from '@vio/core/src/offer/types/OfferLayout'
import {hexToRGBAString} from '@vio/core/src/utils/css'
import {PriceWatchDetailedToggle} from '@vio/shared/src/priceWatch/components/PriceWatchDetailedToggle'

import {useTopOfferDelay} from '../Offer/hooks/useTopOfferDelay'

const Wrapper = styled.div`
  border-radius: ${cssTheme.layout.radius.md};
  padding: 0;
  &.--border {
    border-width: 1px;
    border-style: solid;
    border-color: ${cssTheme.colors.border.neutral.secondary};
  }
  &.--with-hotel-card,
  &.--with-price-insights-banner {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-top: none;
  }

  &.--special-variant {
    border-radius: ${cssTheme.layout.radius.xl};
  }

  &.--with-deal-freeze-banner {
    border-color: ${cssTheme.colors.border.brand.tertiary};
    overflow: hidden;
  }

  .--hide-top-border {
    border-top: none;
  }
`

const PrimaryWrapper = styled.section`
  border: 1px solid ${cssTheme.colors.border.brand.tertiary};
  background: ${hexToRGBAString(
    cssTheme.colors.background.brand.tertiary,
    cssTheme.opacity.o50
  )};
  border-radius: ${cssTheme.layout.radius.md};
  padding: ${cssTheme.layout.spacing.s300} ${cssTheme.layout.spacing.s400}
    ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s400};

  &.--has-margin-bottom {
    margin-bottom: ${cssTheme.layout.spacing.s100};
  }

  &.--with-deal-freeze-banner-and-one-offer {
    margin-bottom: 0;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  }

  /* Just to have the constancy between old offers designs and new offers */
  &.--has-secondary-offers {
    margin: -1px -1px 0 -1px;
  }

  &.--with-hotel-card,
  &.--with-price-insights-banner {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-top: none;
  }

  &.--with-price-insights-banner {
    border-color: ${cssTheme.colors.brand.c600};
  }

  &.--special-variant {
    border-radius: ${cssTheme.layout.radius.xl};
    border-width: 1.5px;
    border-style: solid;
    border-color: ${cssTheme.colors.border.brand.tertiary};

    padding-top: ${cssTheme.layout.spacing.s400};
    padding-bottom: ${cssTheme.layout.spacing.s500};
    padding-right: ${cssTheme.layout.spacing.s500};
    padding-left: ${cssTheme.layout.spacing.s500};
  }
`

const DealFreezeBannerWrapper = styled.div`
  &.--with-border {
    border: 1px solid ${cssTheme.colors.border.brand.tertiary};
    border-top: 0;
    overflow: hidden;
    border-bottom-left-radius: ${cssTheme.layout.radius.md};
    border-bottom-right-radius: ${cssTheme.layout.radius.md};
  }
`

interface MainProps {
  isLoading?: boolean
  offerEntity?: HotelOfferEntity
  component?: string
  primaryOfferVariant: OfferPrimaryLayoutVariant
  onSeeMoreClick?: () => void
  primaryOfferRef?: React.Ref<HTMLElement>
  /** Allows to pass a component that will be rendered as unavailability component */
  unavailabilitySlot?: React.ReactNode
  primaryOfferShouldShowTaxation?: boolean
  withHotelCard?: boolean
  isPrimaryOfferMemberDeal?: boolean
  shouldRenderUnavailabilitySlot?: boolean
  page?: Page
  showCashback?: boolean
  cashbackVariant?: 'short' | 'long'
  /** Hide secondary offers in OTA mode */
  shouldHideSecondaryOffers?: boolean
  /** Different styles for OfferBox */
  offerBoxVariant?: 'original' | 'special'
  /** Flatten top corners/border so a PriceInsightsBanner can sit flush above the OfferBox */
  withPriceInsightsBanner?: boolean
  dealFreezeBannerSlot?: React.ReactNode // Added as a part of c747927f-deal-freeze-srp
  /**
   * Sits below the price alert row. Rendered as passed, without a wrapper, so
   * an experiment that decides not to render leaves no empty box behind.
   * Added as part of 27d5ef4a-call-to-book-anchor.
   */
  callToBookSlot?: React.ReactNode
  withGreatDealTag?: boolean
}

interface LoadingProps {
  isLoading: true
  primaryOfferVariant: OfferPrimaryLayoutVariant
  offerEntity?: never
  component?: never
  onSeeMoreClick?: never
  primaryOfferRef?: never
  unavailabilitySlot?: never
  primaryOfferShouldShowTaxation?: never
  withHotelCard?: never
  isPrimaryOfferMemberDeal?: never
  shouldRenderUnavailabilitySlot?: never
  isAnchor?: boolean
  page?: Page
  showCashback?: never
  cashbackVariant?: never
  /** Hide secondary offers in OTA mode */
  shouldHideSecondaryOffers?: boolean
  offerBoxVariant?: 'original' | 'special'
  /** Flatten top corners/border so a PriceInsightsBanner can sit flush above the OfferBox */
  withPriceInsightsBanner?: boolean
  dealFreezeBannerSlot?: never
  callToBookSlot?: never
  withGreatDealTag?: boolean
}

const OfferBoxLoader = ({
  withHotelCard = false,
  withPriceInsightsBanner = false,
  primaryOfferRef,
  primaryOfferVariant,
  onSeeMoreClick,
  shouldHideSecondaryOffers
}: Partial<MainProps> | LoadingProps) => {
  return (
    <Wrapper
      className={cx(
        '--border',
        withHotelCard && '--with-hotel-card',
        withPriceInsightsBanner && '--with-price-insights-banner'
      )}
    >
      <PrimaryWrapper
        data-id="loader"
        ref={primaryOfferRef}
        className={cx(
          withHotelCard && '--with-hotel-card',
          withPriceInsightsBanner && '--with-price-insights-banner'
        )}
      >
        <PrimaryOffer isLoading variant={primaryOfferVariant} />
      </PrimaryWrapper>
      {!shouldHideSecondaryOffers && (
        <SecondaryOfferList
          isLoading
          variant="vertical"
          nLoaders={onSeeMoreClick ? 3 : 2}
        />
      )}
    </Wrapper>
  )
}

const OfferBoxComponent: React.FC<MainProps | LoadingProps> = ({
  offerEntity,
  isLoading,
  component,
  primaryOfferVariant,
  onSeeMoreClick,
  primaryOfferRef,
  unavailabilitySlot = <></>,
  primaryOfferShouldShowTaxation = true,
  withHotelCard = false,
  shouldRenderUnavailabilitySlot = false,
  page = Page.Search,
  showCashback,
  cashbackVariant = 'long',
  shouldHideSecondaryOffers = false,
  offerBoxVariant = 'original',
  withPriceInsightsBanner = false,
  dealFreezeBannerSlot,
  callToBookSlot,
  withGreatDealTag
}) => {
  const {id: hotelId = ''} = offerEntity || {}
  const isAnchor = useSelector(state => getIsAnchorHotel(state, hotelId))
  const showAnchorOfferLoader = useTopOfferDelay({isAnchor, page})
  const hotel = useSelector(state => getHotel(state, hotelId))
  const isAnchorComplete = useSelector(getAnchorComplete)
  const hasDealFreezeBanner = Boolean(dealFreezeBannerSlot)

  const shouldShowPriceWatchToggle =
    page === Page.Search && isAnchor && isAnchorComplete && !hasDealFreezeBanner

  const {topOffers, offerVariant, anchorPrice, anchorPriceTotal} =
    useOfferContainer({
      offerEntity,
      hotelId,
      withGreatDealTag
    })

  const availableOffersCount = offerEntity?.availableOffersCount || 1

  const {
    splitBookingOffers,
    showSplitBookingLoader,
    hasSplitBookingOffer,
    splitBookingPosition,
    offerPositionToRemove,
    splitBookingBundle,
    hasPrimarySplitBookingOffer
  } = useSelector(state => getSplitBookingDetails(state, hotelId))

  const hasTopOffers = topOffers.length > 0
  const primaryOffer = topOffers?.[0]
  const secondaryOffers = useMemo(() => {
    const offers = getSecondaryOffers({
      topOffers,
      hasSplitBookingOffer: hasSplitBookingOffer,
      splitBookingPosition,
      offerPositionToRemove
    })

    return offers
  }, [
    topOffers,
    hasSplitBookingOffer,
    splitBookingPosition,
    offerPositionToRemove
  ])

  const hasSecondaryOffers =
    !shouldHideSecondaryOffers && secondaryOffers.length > 0

  const renderSplitBookingPrimaryOffer = useCallback(() => {
    return (
      <PrimaryOffer
        isSplitBooking
        anchorPrice={anchorPrice}
        anchorPriceTotal={anchorPriceTotal}
        hotelId={hotelId}
        shouldShowTaxation={primaryOfferShouldShowTaxation}
        shouldShowTaxationAndNightly
        variant={primaryOfferVariant}
        splitBookingOffers={splitBookingOffers}
        splitBookingBundle={splitBookingBundle}
        sourceComponent={component}
        showCashback={showCashback}
      />
    )
  }, [
    anchorPrice,
    anchorPriceTotal,
    hotelId,
    primaryOfferShouldShowTaxation,
    primaryOfferVariant,
    splitBookingOffers,
    splitBookingBundle,
    component,
    showCashback
  ])

  const renderPrimaryOffer = useCallback(() => {
    // Show loader
    if (isAnchor && (showSplitBookingLoader || showAnchorOfferLoader)) {
      return <PrimaryOffer variant={primaryOfferVariant} isLoading />
    }

    if (hasPrimarySplitBookingOffer) {
      return renderSplitBookingPrimaryOffer()
    }

    return (
      <PrimaryOffer
        anchorPrice={anchorPrice}
        anchorPriceTotal={anchorPriceTotal}
        hotelId={hotelId}
        offer={primaryOffer}
        offerVariant={offerVariant}
        shouldShowTaxation={primaryOfferShouldShowTaxation}
        shouldShowTaxationAndNightly
        variant={primaryOfferVariant}
        hasInteraction
        sourceComponent={component}
        showCashback={showCashback}
        showPriceIndication={offerBoxVariant === 'special'}
      />
    )
  }, [
    anchorPrice,
    anchorPriceTotal,
    component,
    hasPrimarySplitBookingOffer,
    hotelId,
    isAnchor,
    showAnchorOfferLoader,
    offerBoxVariant,
    offerVariant,
    primaryOffer,
    primaryOfferShouldShowTaxation,
    primaryOfferVariant,
    renderSplitBookingPrimaryOffer,
    showCashback,
    showSplitBookingLoader
  ])

  const viewMoreDealsButton = useMemo(() => {
    if (!onSeeMoreClick || availableOffersCount < 3) return

    return <MoreDealsButtonVertical onClick={onSeeMoreClick} />
  }, [availableOffersCount, onSeeMoreClick])

  // Has no top offers
  if (!hasTopOffers) {
    // Show loader
    if (isLoading || showSplitBookingLoader || showAnchorOfferLoader) {
      return (
        <OfferBoxLoader
          withHotelCard={withHotelCard}
          withPriceInsightsBanner={withPriceInsightsBanner}
          primaryOfferRef={primaryOfferRef}
          primaryOfferVariant={primaryOfferVariant}
          shouldHideSecondaryOffers={shouldHideSecondaryOffers}
          onSeeMoreClick={onSeeMoreClick}
        />
      )
    }

    // Has no offers but splitbooking and search has been done
    if (hasSplitBookingOffer) {
      return (
        <Wrapper
          className={cx(
            withHotelCard && '--with-hotel-card',
            withPriceInsightsBanner && '--with-price-insights-banner',
            offerBoxVariant === 'special' && '--special-variant'
          )}
        >
          <PrimaryWrapper
            ref={primaryOfferRef}
            className={cx(
              withHotelCard && '--with-hotel-card',
              withPriceInsightsBanner && '--with-price-insights-banner',
              offerBoxVariant === 'special' && '--special-variant',
              '--has-margin-bottom',
              hasSecondaryOffers && '--has-secondary-offers'
            )}
          >
            {renderSplitBookingPrimaryOffer()}
          </PrimaryWrapper>
        </Wrapper>
      )
    }

    // No offers and no splitbooking offers then we show unavailability
    if (shouldRenderUnavailabilitySlot) {
      return <>{unavailabilitySlot}</>
    }

    return (
      <OfferBoxLoader
        withHotelCard={withHotelCard}
        withPriceInsightsBanner={withPriceInsightsBanner}
        primaryOfferRef={primaryOfferRef}
        primaryOfferVariant={primaryOfferVariant}
        shouldHideSecondaryOffers={shouldHideSecondaryOffers}
        onSeeMoreClick={onSeeMoreClick}
      />
    )
  }

  // Has top offers
  return (
    <Wrapper
      className={cx(
        topOffers.length > 1 && '--border',
        withHotelCard && '--with-hotel-card',
        withPriceInsightsBanner && '--with-price-insights-banner',
        offerBoxVariant === 'special' && '--special-variant',
        hasDealFreezeBanner && '--with-deal-freeze-banner'
      )}
    >
      <PrimaryWrapper
        ref={primaryOfferRef}
        className={cx(
          withHotelCard && '--with-hotel-card',
          withPriceInsightsBanner && '--with-price-insights-banner',
          offerBoxVariant === 'special' && '--special-variant',
          '--has-margin-bottom',
          hasSecondaryOffers && '--has-secondary-offers',
          !hasSecondaryOffers &&
            hasDealFreezeBanner &&
            '--with-deal-freeze-banner-and-one-offer'
        )}
      >
        {renderPrimaryOffer()}
      </PrimaryWrapper>
      {!shouldHideSecondaryOffers && (
        <SecondaryOfferList
          variant="vertical"
          anchorPrice={anchorPrice}
          anchorPriceTotal={anchorPriceTotal}
          splitBookingPosition={splitBookingPosition}
          splitBookingBundle={splitBookingBundle}
          hotelId={hotelId}
          secondaryOffers={secondaryOffers}
          sourceComponent={component}
          splitBookingOffers={splitBookingOffers}
          showSplitBookingLoader={showSplitBookingLoader}
          viewMoreSlot={viewMoreDealsButton}
          showCashback={showCashback}
          cashbackVariant={cashbackVariant}
          showPriceIndication={offerBoxVariant === 'special'}
        />
      )}
      {hasDealFreezeBanner && (
        <DealFreezeBannerWrapper
          className={cx(!hasSecondaryOffers && '--with-border')}
        >
          {dealFreezeBannerSlot}
        </DealFreezeBannerWrapper>
      )}
      {shouldShowPriceWatchToggle && (
        <PriceWatchCTAWrapper
          className={cx(!hasSecondaryOffers && '--hide-top-border')}
          hotel={hotel}
          ctaComponent={PriceWatchDetailedToggle}
          componentName="PriceWatchDetailedToggle"
          searchIsCompleted
        />
      )}
      {callToBookSlot}
    </Wrapper>
  )
}

export const OfferBox = React.memo(OfferBoxComponent)
